/* eslint-disable react-refresh/only-export-components -- deliberately a mixed module: shared tokens/helpers exported beside components; HMR full-reload is acceptable here. */
import {
    deleteTranchePayment,
    getOrderPayments,
    getShipmentPayments,
    recordTranchePayment,
    updateTrancheOverride,
    type OrderPaymentView,
    type OrderTrancheView,
} from '@api/mother-order';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { DateSinglePicker } from '@components/ui/DateRange';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    Group,
    Modal,
    NumberInput,
    Text,
    TextInput,
} from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useState } from 'react';

import {
    formatDateDisplay,
    formatMoney,
    PanelField,
    panelSectionLabel,
    SectionHeader,
    StatusBadge,
    textColors,
    todayIso,
} from '../procurementOrders/shared';

import styles from './PaymentScheduleSection.module.css';

const errMessage = (e: any, fallback: string): string =>
    e?.error?.message || e?.message || fallback;

// ─── Mark paid ──────────────────────────────────────────────────

/** Generic mark-paid target — built from an OrderTrancheView or a PayableItem. */
export type MarkPaidTarget = {
    level: 'MOTHER' | 'SHIPMENT' | 'STANDALONE';
    /** Route target: mother uuid for MOTHER, purchase-order id otherwise. */
    orderId: string;
    trancheId: string;
    label: string;
    amount: number;
    paidAmount: number;
    outstanding: number;
    payeeName?: string | null;
};

export const trancheViewToTarget = (
    row: OrderTrancheView,
    motherOrderId: string,
): MarkPaidTarget => ({
    level: row.level,
    orderId: row.level === 'MOTHER' ? motherOrderId : (row.shipmentPoId ?? ''),
    trancheId: row.tranche.id,
    label: row.tranche.label,
    amount: row.tranche.amount,
    paidAmount: row.state.paidAmount,
    outstanding: row.state.outstanding,
    payeeName:
        row.tranche.payee.kind === 'CREDIT_PARTNER'
            ? (row.tranche.payee.name ?? 'credit partner')
            : row.tranche.payee.kind === 'FREIGHT_FORWARDER'
              ? (row.tranche.payee.name ?? 'forwarder')
              : null,
});

export const MarkPaidModal: FC<{
    target: MarkPaidTarget | null;
    currency: string;
    onClose: () => void;
    onSaved: () => void;
}> = ({ target, currency, onClose, onSaved }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [amount, setAmount] = useState<number | ''>('');
    const [paidOn, setPaidOn] = useState(todayIso());
    const [method, setMethod] = useState('Wire');
    const [reference, setReference] = useState('');
    const [saving, setSaving] = useState(false);

    // Prefill with the outstanding amount whenever a new tranche opens.
    const rowKey = target?.trancheId ?? null;
    useEffect(() => {
        if (target) {
            setAmount(Math.max(0, target.outstanding) || '');
            setPaidOn(todayIso());
            setMethod('Wire');
            setReference('');
            setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowKey]);

    const save = async () => {
        if (!target || amount === '' || Number(amount) <= 0) {
            showToastError({ title: 'Enter the paid amount' });
            return;
        }
        setSaving(true);
        try {
            await recordTranchePayment(
                target.level,
                target.orderId,
                target.trancheId,
                {
                    amount: Number(amount),
                    paidOn,
                    method: method.trim() || undefined,
                    reference: reference.trim() || undefined,
                },
            );
            showToastSuccess({ title: 'Payment recorded' });
            onSaved();
            onClose();
        } catch (e: any) {
            showToastError({
                title: 'Could not record payment',
                subtitle: errMessage(e, 'Please retry.'),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={!!target}
            onClose={onClose}
            title={target ? `Mark paid — ${target.label}` : ''}
            size="sm"
            centered
        >
            {target && (
                <div className="space-y-3">
                    <Text size="12px" c={textColors.secondary}>
                        {formatMoney(target.amount, currency)} scheduled ·{' '}
                        {formatMoney(target.paidAmount, currency)} paid ·{' '}
                        {formatMoney(Math.max(0, target.outstanding), currency)}{' '}
                        outstanding
                        {target.payeeName && ` · payable to ${target.payeeName}`}
                    </Text>
                    <NumberInput
                        label="Amount"
                        value={amount}
                        onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
                        min={0.01}
                        hideControls
                    />
                    <div>
                        <Text size="11px" fw={600} c={textColors.secondary} mb={4}>
                            Paid on
                        </Text>
                        <DateSinglePicker
                            value={paidOn}
                            onChange={(v) => setPaidOn(v || todayIso())}
                        />
                    </div>
                    <TextInput
                        label="Method"
                        value={method}
                        onChange={(e) => setMethod(e.currentTarget.value)}
                        placeholder="Wire / NEFT / …"
                    />
                    <TextInput
                        label="Reference (UTR)"
                        value={reference}
                        onChange={(e) => setReference(e.currentTarget.value)}
                        placeholder="Optional"
                    />
                    <Group justify="flex-end" gap={8}>
                        <Button variant="default" size="xs" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button size="xs" loading={saving} onClick={save}>
                            Record payment
                        </Button>
                    </Group>
                </div>
            )}
        </Modal>
    );
};

// ─── Tranche overrides (amount / due-date pin) ──────────────────

const TrancheEditModal: FC<{
    row: OrderTrancheView | null;
    orderId: string;
    currency: string;
    onClose: () => void;
    onSaved: () => void;
}> = ({ row, orderId, currency, onClose, onSaved }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [amount, setAmount] = useState<number | ''>('');
    const [dueDate, setDueDate] = useState('');
    const [saving, setSaving] = useState(false);

    const rowKey = row?.tranche.id ?? null;
    useEffect(() => {
        if (row) {
            setAmount(row.tranche.amount);
            setDueDate(row.tranche.dueDateOverride ?? '');
            setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowKey]);

    const save = async () => {
        if (!row) return;
        setSaving(true);
        try {
            await updateTrancheOverride(
                row.level,
                row.level === 'MOTHER' ? orderId : (row.shipmentPoId ?? ''),
                row.tranche.id,
                {
                    // amount unchanged → don't touch the override flag;
                    // cleared field → revert to %-derived
                    amount:
                        amount === ''
                            ? null
                            : Number(amount) === row.tranche.amount &&
                                !row.tranche.amountOverridden
                              ? undefined
                              : Number(amount),
                    dueDateOverride: dueDate || null,
                },
            );
            showToastSuccess({ title: 'Tranche updated' });
            onSaved();
            onClose();
        } catch (e: any) {
            showToastError({
                title: 'Could not update tranche',
                subtitle: errMessage(e, 'Please retry.'),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={!!row}
            onClose={onClose}
            title={row ? `Edit — ${row.tranche.label}` : ''}
            size="sm"
            centered
        >
            {row && (
                <div className="space-y-3">
                    <NumberInput
                        label={`Amount (${currency}) — clear to revert to ${row.tranche.percentage ?? '—'}% of value`}
                        value={amount}
                        onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
                        min={0}
                        hideControls
                    />
                    <div>
                        <Text size="11px" fw={600} c={textColors.secondary} mb={4}>
                            Pin due date (clear to derive from{' '}
                            {row.tranche.anchor.toLowerCase().replace('_', ' ')}
                            {row.tranche.offsetDays
                                ? ` + ${row.tranche.offsetDays}d`
                                : ''}
                            )
                        </Text>
                        <DateSinglePicker
                            value={dueDate}
                            onChange={(v) => setDueDate(v || '')}
                            clearable
                        />
                    </div>
                    <Group justify="flex-end" gap={8}>
                        <Button variant="default" size="xs" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button size="xs" loading={saving} onClick={save}>
                            Save
                        </Button>
                    </Group>
                </div>
            )}
        </Modal>
    );
};

// ─── The section ────────────────────────────────────────────────

/**
 * PAYMENTS section on the order detail: balance strip, advance context,
 * every tranche across the family with derived state, forecast residual.
 * Everything comes from GET /orders/:id/payments — derived fresh per read.
 */
export const PaymentScheduleSection: FC<{
    orderId: string;
    currency: string;
}> = ({ orderId, currency }) => {
    const queryClient = useQueryClient();
    const { showToastSuccess, showToastError } = useToaster();
    const { data: view } = useQuery(
        ['order-payments', orderId],
        () => getOrderPayments(orderId),
        { enabled: !!orderId },
    );
    const [markPaidRow, setMarkPaidRow] = useState<OrderTrancheView | null>(null);
    const [editRow, setEditRow] = useState<OrderTrancheView | null>(null);

    const refresh = () =>
        void queryClient.invalidateQueries(['order-payments', orderId]);

    const undoPayment = async (row: OrderTrancheView) => {
        const last = row.tranche.payments[row.tranche.payments.length - 1];
        if (!last) return;
        try {
            await deleteTranchePayment(
                row.level,
                row.level === 'MOTHER' ? orderId : (row.shipmentPoId ?? ''),
                row.tranche.id,
                last.id,
            );
            showToastSuccess({ title: 'Payment removed' });
            refresh();
        } catch (e: any) {
            showToastError({
                title: 'Could not remove payment',
                subtitle: errMessage(e, 'Please retry.'),
            });
        }
    };

    const columns: DataSheetColumn<OrderTrancheView>[] = useMemo(
        () => [
            {
                key: 'label',
                header: 'Payment',
                width: 170,
                sticky: true,
                render: (_: unknown, r: OrderTrancheView) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>
                        {r.tranche.label}
                        {r.level === 'SHIPMENT' && (
                            <span style={{ color: textColors.muted, fontSize: 11 }}>
                                {' '}
                                · {r.shipmentRef}
                            </span>
                        )}
                    </span>
                ),
            },
            {
                key: 'payee',
                header: 'Payee',
                width: 130,
                render: (_: unknown, r: OrderTrancheView) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {r.tranche.payee.kind === 'CREDIT_PARTNER'
                            ? (r.tranche.payee.name ?? 'Credit partner')
                            : r.tranche.payee.kind === 'FREIGHT_FORWARDER'
                              ? `${r.tranche.payee.name ?? 'Forwarder'} (freight)`
                              : 'Vendor'}
                    </span>
                ),
            },
            {
                key: 'amount',
                header: 'Amount',
                width: 110,
                align: 'right' as const,
                render: (_: unknown, r: OrderTrancheView) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>
                        {formatMoney(r.tranche.amount, currency)}
                        {r.tranche.amountOverridden && (
                            <span title="Manually overridden"> ✎</span>
                        )}
                    </span>
                ),
            },
            {
                key: 'due',
                header: 'Due',
                width: 130,
                render: (_: unknown, r: OrderTrancheView) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {r.state.dueDate ? formatDateDisplay(r.state.dueDate) : '—'}
                        {r.state.dueDateSource === 'EXPECTED' && (
                            <span
                                style={{ color: textColors.muted }}
                                title="Projected from the expected timeline"
                            >
                                {' '}
                                ~
                            </span>
                        )}
                        {r.state.dueDateSource === 'OVERRIDE' && (
                            <span title="Pinned manually"> 📌</span>
                        )}
                    </span>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                width: 120,
                render: (_: unknown, r: OrderTrancheView) => (
                    <StatusBadge status={r.state.status} />
                ),
            },
            {
                key: 'paid',
                header: 'Paid',
                width: 100,
                align: 'right' as const,
                render: (_: unknown, r: OrderTrancheView) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {r.state.paidAmount > 0
                            ? formatMoney(r.state.paidAmount, currency)
                            : '—'}
                    </span>
                ),
            },
            {
                key: 'covered',
                header: 'Adv. cover',
                width: 100,
                align: 'right' as const,
                render: (_: unknown, r: OrderTrancheView) => (
                    <span style={{ fontSize: 12, color: textColors.muted }}>
                        {r.coveredByAdvance
                            ? formatMoney(r.coveredByAdvance, currency)
                            : '—'}
                    </span>
                ),
            },
            {
                key: 'actions',
                header: '',
                width: 170,
                render: (_: unknown, r: OrderTrancheView) => (
                    <Group gap={6} wrap="nowrap">
                        {r.state.status !== 'PAID' &&
                            r.state.status !== 'CANCELLED' && (
                                <Button
                                    size="compact-xs"
                                    variant="default"
                                    onClick={() => setMarkPaidRow(r)}
                                >
                                    Mark paid
                                </Button>
                            )}
                        {r.tranche.payments.length > 0 &&
                            r.state.status !== 'CANCELLED' && (
                                <Button
                                    size="compact-xs"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => undoPayment(r)}
                                    title="Remove the last recorded payment (corrections are delete + re-add)."
                                >
                                    Undo
                                </Button>
                            )}
                        {r.state.status !== 'CANCELLED' && (
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                onClick={() => setEditRow(r)}
                            >
                                Edit
                            </Button>
                        )}
                    </Group>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currency, orderId],
    );

    if (!view) return null;

    const strip = buildStrip(view, currency);

    return (
        <>
            <SectionHeader>PAYMENTS</SectionHeader>
            <div className={styles.strip}>
                {strip.map((s) => (
                    <div key={s.label} className={styles.stat}>
                        <Text size="10px" fw={700} c={textColors.muted} className={styles.statLabel}>
                            {s.label.toUpperCase()}
                        </Text>
                        <Text size="15px" fw={600} c={s.color ?? textColors.primary} className={styles.statValue}>
                            {s.value}
                        </Text>
                    </div>
                ))}
            </div>
            {view.advance && (
                <Text size="11px" c={textColors.muted} mb={6}>
                    Advance {view.advance.percentage}% · paid{' '}
                    {formatMoney(view.advance.paid, currency)} ·{' '}
                    {formatMoney(Math.max(0, view.advance.unallocated), currency)}{' '}
                    not yet consumed by shipments
                    {view.route === 'CREDIT_PARTNER' &&
                        view.creditPartnerName &&
                        ` · balance routed via ${view.creditPartnerName}`}
                </Text>
            )}
            <DataSheet<OrderTrancheView>
                data={view.tranches}
                columns={columns}
                bare
                hideHeaderBar
            />
            {view.forecastResidual && (
                <Text size="11px" c={textColors.muted} mt={6}>
                    Still in production: {formatMoney(view.forecastResidual.value, currency)}{' '}
                    of goods →{' '}
                    {view.forecastResidual.tranches
                        .map((t) => `${t.label} ${formatMoney(t.amount, currency)}`)
                        .join(' · ')}{' '}
                    (forecast — created as shipments are added)
                </Text>
            )}

            <MarkPaidModal
                target={markPaidRow ? trancheViewToTarget(markPaidRow, orderId) : null}
                currency={currency}
                onClose={() => setMarkPaidRow(null)}
                onSaved={refresh}
            />
            <TrancheEditModal
                row={editRow}
                orderId={orderId}
                currency={currency}
                onClose={() => setEditRow(null)}
                onSaved={refresh}
            />
        </>
    );
};

function buildStrip(
    view: OrderPaymentView,
    currency: string,
): Array<{ label: string; value: string; color?: string }> {
    const t = view.totals;
    const strip = [
        { label: 'Total', value: formatMoney(t.orderTotal, currency) },
        { label: 'Paid', value: formatMoney(t.paid, currency), color: 'rgb(22, 130, 93)' },
        { label: 'Due to vendor', value: formatMoney(t.dueToVendor, currency) },
    ];
    if (t.dueToCreditPartner > 0) {
        strip.push({
            label: `Due to ${view.creditPartnerName ?? 'credit partner'}`,
            value: formatMoney(t.dueToCreditPartner, currency),
        });
    }
    if ((t.dueToForwarder ?? 0) > 0) {
        strip.push({ label: 'Due to forwarder', value: formatMoney(t.dueToForwarder, currency) });
    }
    if (t.overdue > 0) {
        strip.push({
            label: 'Overdue',
            value: formatMoney(t.overdue, currency),
            color: 'rgb(205, 101, 101)',
        });
    }
    if (t.unscheduled > 0) {
        strip.push({
            label: 'Unscheduled',
            value: formatMoney(t.unscheduled, currency),
            color: 'rgb(124, 95, 240)',
        });
    }
    return strip;
}

// ─── Shipment panel block ───────────────────────────────────────

/**
 * The PAYMENTS block inside the shipment side panel: payment summary strip +
 * this shipment's tranches with mark-paid. Mother-linked shipments read the
 * cached order-payments query; standalone synced POs read their own
 * PO-payments view — same shapes, same rendering.
 */
export const ShipmentPaymentsBlock: FC<{
    motherOrderId: string | null | undefined;
    shipmentPoId: string;
    currency: string;
}> = ({ motherOrderId, shipmentPoId, currency }) => {
    const queryClient = useQueryClient();
    const { data: orderView } = useQuery(
        ['order-payments', motherOrderId],
        () => getOrderPayments(motherOrderId!),
        { enabled: !!motherOrderId },
    );
    const { data: poView } = useQuery(
        ['po-payments', shipmentPoId],
        () => getShipmentPayments(shipmentPoId),
        { enabled: !motherOrderId && !!shipmentPoId },
    );
    const [markPaidRow, setMarkPaidRow] = useState<OrderTrancheView | null>(null);

    const view = motherOrderId ? orderView : poView;
    if (!view) return null;
    const rows = view.tranches.filter((t) => t.shipmentPoId === shipmentPoId);
    if (rows.length === 0) return null;

    const refresh = () => {
        if (motherOrderId) {
            void queryClient.invalidateQueries(['order-payments', motherOrderId]);
        } else {
            void queryClient.invalidateQueries(['po-payments', shipmentPoId]);
        }
        void queryClient.invalidateQueries(['payables']);
    };

    // Summary numbers: for standalone POs the view IS this shipment; for
    // mother-linked shipments, fold just this shipment's rows.
    const paid = rows.reduce((sum, r) => sum + r.state.paidAmount, 0);
    const outstanding = rows.reduce(
        (sum, r) =>
            sum +
            (r.state.status === 'CANCELLED'
                ? 0
                : Math.max(0, r.state.outstanding)),
        0,
    );
    const scheduled = rows.reduce((sum, r) => sum + r.tranche.amount, 0);
    const termsText = rows
        .map((r) =>
            r.tranche.percentage
                ? `${r.tranche.label} ${r.tranche.percentage}%`
                : r.tranche.label,
        )
        .join(' · ');
    const partner = rows.find(
        (r) => r.tranche.payee.kind === 'CREDIT_PARTNER',
    )?.tranche.payee;

    const trancheColumns: DataSheetColumn<OrderTrancheView>[] = [
        {
            key: 'label',
            header: 'Payment',
            width: 170,
            render: (_: unknown, r: OrderTrancheView) => (
                <span style={{ fontSize: 13, fontWeight: 500, color: textColors.primary }}>
                    {r.tranche.label}
                    {r.tranche.payee.kind !== 'VENDOR' && (
                        <span style={{ color: textColors.muted, fontSize: 11 }}>
                            {' '}
                            → {r.tranche.payee.name ?? (r.tranche.payee.kind === 'FREIGHT_FORWARDER' ? 'forwarder' : 'partner')}
                        </span>
                    )}
                </span>
            ),
        },
        {
            key: 'amount',
            header: 'Amount',
            width: 120,
            align: 'right' as const,
            render: (_: unknown, r: OrderTrancheView) => (
                <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: textColors.primary }}>
                    {formatMoney(r.tranche.amount, currency)}
                </span>
            ),
        },
        {
            key: 'due',
            header: 'Due',
            width: 130,
            render: (_: unknown, r: OrderTrancheView) => (
                <span style={{ fontSize: 12, color: textColors.secondary, fontVariantNumeric: 'tabular-nums' }}>
                    {r.state.dueDate ? formatDateDisplay(r.state.dueDate) : '—'}
                    {r.state.dueDateSource === 'EXPECTED' && (
                        <span style={{ color: textColors.muted }} title="Projected from the expected timeline"> ~</span>
                    )}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            width: 110,
            render: (_: unknown, r: OrderTrancheView) => (
                <StatusBadge status={r.state.status} />
            ),
        },
        {
            key: 'action',
            header: '',
            width: 110,
            align: 'right' as const,
            render: (_: unknown, r: OrderTrancheView) =>
                r.state.status !== 'PAID' && r.state.status !== 'CANCELLED' ? (
                    <Button
                        size="compact-xs"
                        variant="default"
                        onClick={() => setMarkPaidRow(r)}
                    >
                        Mark paid
                    </Button>
                ) : null,
        },
    ];

    return (
        <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 20 }}>
            <div style={panelSectionLabel}>Payments</div>
            <div style={{ display: 'flex', gap: 40, marginBottom: 16 }}>
                <PanelField label="Scheduled" value={formatMoney(scheduled, currency)} />
                <PanelField label="Paid" value={paid > 0 ? formatMoney(paid, currency) : '—'} />
                <PanelField
                    label="Outstanding"
                    value={outstanding > 0 ? formatMoney(outstanding, currency) : '—'}
                />
                <PanelField
                    label="Terms"
                    flex={1.6}
                    value={
                        termsText +
                        (partner
                            ? ` · via ${partner.name ?? 'credit partner'} (+${partner.premiumPct ?? 0}%, ${partner.creditDays ?? 0}d)`
                            : '')
                    }
                />
            </div>
            <DataSheet<OrderTrancheView>
                data={rows}
                columns={trancheColumns}
                getRowKey={(r) => r.tranche.id}
                bare
                hideHeaderBar
            />
            {rows[0]?.coveredByAdvance ? (
                <Text size="11px" c={textColors.muted} mt={6}>
                    Covered by advance: {formatMoney(rows[0].coveredByAdvance, currency)}
                </Text>
            ) : null}
            <MarkPaidModal
                target={
                    markPaidRow
                        ? {
                              ...trancheViewToTarget(
                                  markPaidRow,
                                  motherOrderId ?? shipmentPoId,
                              ),
                              level: motherOrderId ? markPaidRow.level : 'STANDALONE',
                          }
                        : null
                }
                currency={currency}
                onClose={() => setMarkPaidRow(null)}
                onSaved={refresh}
            />
        </div>
    );
};
